import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

export const CURRENCY_META = {
  EUR: { code: "EUR", symbol: "€", label: "Euro", flag: "🇪🇺" },
  USD: { code: "USD", symbol: "$", label: "US Dollar", flag: "🇺🇸" },
  GBP: { code: "GBP", symbol: "£", label: "British Pound", flag: "🇬🇧" },
  ALL: { code: "ALL", symbol: "L", label: "Albanian Lek", flag: "🇦🇱" },
  CHF: { code: "CHF", symbol: "₣", label: "Swiss Franc", flag: "🇨🇭" },
  PLN: { code: "PLN", symbol: "zł", label: "Polish Złoty", flag: "🇵🇱" },
};

const Ctx = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("berdhill_currency") || "EUR");
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, GBP: 0.85, ALL: 99.5 });
  const [enabled, setEnabled] = useState(["EUR", "USD", "GBP", "ALL"]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const fx = r.data?.fx_rates?.value;
      if (fx) {
        try { setRates(JSON.parse(fx)); } catch {/* noop */}
      }
      const en = r.data?.currencies_enabled?.value;
      if (en) setEnabled(en.split(",").map((s) => s.trim()).filter(Boolean));
      const def = r.data?.currency_default?.value;
      if (def && !localStorage.getItem("berdhill_currency")) setCurrencyState(def);
    }).catch(() => {});
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem("berdhill_currency", c);
  }, []);

  // Convert price from EUR (storage currency) → target
  const convert = useCallback((eur) => {
    const r = rates[currency] ?? 1;
    return Number(eur) * r;
  }, [rates, currency]);

  const format = useCallback((eur, opts = {}) => {
    const v = convert(eur);
    const meta = CURRENCY_META[currency] || CURRENCY_META.EUR;
    const fractionDigits = currency === "ALL" ? 0 : 2;
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(v);
    if (opts.symbolAfter) return `${formatted} ${meta.symbol}`;
    return `${meta.symbol}${formatted}`;
  }, [convert, currency]);

  const value = useMemo(() => ({
    currency, setCurrency, rates, enabled, convert, format, meta: CURRENCY_META[currency] || CURRENCY_META.EUR,
  }), [currency, setCurrency, rates, enabled, convert, format]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCurrency = () => useContext(Ctx) || {
  currency: "EUR",
  format: (v) => `€${Number(v).toFixed(2)}`,
  convert: (v) => v,
  enabled: ["EUR"],
  setCurrency: () => {},
  meta: CURRENCY_META.EUR,
};
